import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  MessageSquare, ShieldAlert, Ban, TrendingUp, CalendarClock,
  Sparkles, FileText, ChevronDown,
} from "lucide-react";
import { adminApi } from "@/lib/adminApi";
import { AdminPageHeader, Stat, Card } from "./_shared";

const ALL_POLLS = "__all__";
const SCOPE_KEY = "ks_dashboard_scope";

export const Dashboard = () => {
  const [polls, setPolls] = useState([]);
  const [scope, setScope] = useState(() => {
    if (typeof window === "undefined") return ALL_POLLS;
    return localStorage.getItem(SCOPE_KEY) || ALL_POLLS;
  });
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  // Load all polls for the picker
  useEffect(() => {
    adminApi.get("/admin/polls").then((r) => setPolls(r.data?.polls || [])).catch(() => { /* still works without picker options */ });
  }, []);

  // Load scoped dashboard
  useEffect(() => {
    setData(null);
    setErr(null);
    const params = scope === ALL_POLLS ? {} : { params: { poll_slug: scope } };
    adminApi.get("/admin/dashboard", params)
      .then((r) => setData(r.data))
      .catch((e) => setErr(e?.response?.data?.detail || "Failed"));
  }, [scope]);

  const handleScopeChange = (v) => {
    setScope(v);
    try { localStorage.setItem(SCOPE_KEY, v); } catch { /* ignore */ }
  };

  if (err) return <div className="text-[#E3000F] font-bold uppercase tracking-[0.25em] text-sm" data-testid="dashboard-error">{err}</div>;

  const t = data?.totals || {};
  const mc = data?.mc_totals;
  const isMc = !!data?.scope?.is_mc;
  const q = data?.current_question || {};

  return (
    <div className="space-y-7" data-testid="admin-dashboard">
      <AdminPageHeader
        eyebrow="OVERVIEW"
        title="Dashboard"
        action={
          <CampaignPicker
            polls={polls}
            value={scope}
            onChange={handleScopeChange}
          />
        }
      />

      {!data ? (
        <div className="text-neutral-400 font-bold uppercase tracking-[0.25em] text-sm">Loading…</div>
      ) : isMc && mc ? (
        // ===== Multiple-choice scope view =====
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="mc-stats">
            <Stat label="Total votes" value={mc.votes_total?.toLocaleString()} testId="mc-stat-votes" accent="#FF9933" ink="#0A0A0A" />
            <Stat label="Votes 24h" value={mc.votes_24h?.toLocaleString()} testId="mc-stat-votes-24h" />
            <Stat label="Comments" value={mc.comments_total?.toLocaleString()} testId="mc-stat-comments" />
            <Stat label="Pending moderation" value={mc.comments_pending?.toLocaleString()} testId="mc-stat-pending" />
          </div>

          <Card title="Active Question" testId="card-active-question">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
              <div>
                <p className="font-display uppercase text-2xl leading-none" style={{ color: "#FF9933" }} data-testid="active-q-title">
                  {q.title || "—"}
                </p>
                <p className="text-sm font-medium text-neutral-300 mt-2 max-w-xl">{q.headline || ""}</p>
                <p className="text-[10px] uppercase font-bold tracking-[0.3em] mt-3 text-neutral-400 flex items-center gap-2">
                  <CalendarClock size={12} strokeWidth={3} />
                  {data.days_remaining != null ? <>Closes in <span style={{ color: "#FF9933" }}>{data.days_remaining}</span> days</> : "No closing date set"}
                </p>
              </div>
              <div className="flex gap-2 shrink-0">
                <Link to={`/p/${data.scope.poll_slug}`} target="_blank" rel="noopener noreferrer" className="brutal-btn-ghost text-xs" data-testid="dashboard-view-poll">View →</Link>
                <Link to="/admin/active-questions" className="brutal-btn" style={{ background: "#FF9933", color: "#0A0A0A", borderColor: "#FF9933" }} data-testid="dashboard-edit-question">Manage →</Link>
              </div>
            </div>
          </Card>

          <Card title="Vote distribution" testId="card-vote-distribution">
            <div className="space-y-3" data-testid="mc-options">
              {(mc.options || []).map((o) => {
                const winning = mc.top_option?.id === o.id;
                return (
                  <div key={o.id} data-testid={`mc-opt-${o.id}`}>
                    <div className="flex items-center justify-between mb-1 gap-3">
                      <span className="text-sm text-neutral-200">
                        {winning && <span style={{ color: "#FF9933" }} className="mr-1">★</span>}
                        {o.label}
                      </span>
                      <span className="text-xs font-bold text-neutral-300 shrink-0">
                        {o.percentage.toFixed(1)}% · {o.votes.toLocaleString()}
                      </span>
                    </div>
                    <div className="h-2 bg-[#1a1a1a] border border-[#2a2a2a]">
                      <div className="h-full transition-all" style={{ width: `${Math.min(100, o.percentage)}%`, background: winning ? "#FF9933" : "#9bc6ff" }} />
                    </div>
                  </div>
                );
              })}
              {mc.votes_total === 0 && <p className="text-sm text-neutral-500">No votes recorded yet.</p>}
            </div>
          </Card>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <QuickLink to="/admin/reports" Icon={FileText} label="Reports" />
            <QuickLink to="/admin/active-questions" Icon={MessageSquare} label="Manage Polls" />
            <QuickLink to="/admin/moderation" Icon={ShieldAlert} label="Moderation" />
            <QuickLink to="/admin/content-studio" Icon={Sparkles} label="Content" />
          </div>
        </>
      ) : (
        // ===== Open-poll / all-polls view (legacy) =====
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Stat label="Approved" value={t.messages_approved?.toLocaleString()} testId="stat-approved" />
            <Stat label="Upvotes total" value={t.upvotes_total?.toLocaleString()} testId="stat-upvotes" accent="#FF9933" ink="#0A0A0A" />
            <Stat label="In 24h" value={t.messages_24h?.toLocaleString()} testId="stat-24h" />
            <Stat label="In 7 days" value={t.messages_7d?.toLocaleString()} testId="stat-7d" />
            <Stat label="Flagged" value={t.messages_flagged?.toLocaleString()} accent="#E3000F" ink="#FFFFFF" testId="stat-flagged" />
            <Stat label="Spam" value={t.messages_spam?.toLocaleString()} testId="stat-spam" />
            <Stat label="Removed" value={t.messages_removed?.toLocaleString()} testId="stat-removed" />
            <Stat label="Upvotes 24h" value={t.upvotes_24h?.toLocaleString()} testId="stat-upvotes-24h" />
          </div>

          <Card title="Active Question" testId="card-active-question">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
              <div>
                <p className="font-display uppercase text-2xl leading-none" style={{ color: "#FF9933" }} data-testid="active-q-title">
                  {q.title || "—"}
                </p>
                <p className="text-sm font-medium text-neutral-300 mt-2 max-w-xl">{q.headline || ""}</p>
                <p className="text-[10px] uppercase font-bold tracking-[0.3em] mt-3 text-neutral-400 flex items-center gap-2">
                  <CalendarClock size={12} strokeWidth={3} />
                  {data.days_remaining != null ? <>Closes in <span style={{ color: "#FF9933" }}>{data.days_remaining}</span> days</> : "No closing date set"}
                </p>
              </div>
              <Link to="/admin/active-questions" className="brutal-btn shrink-0" style={{ background: "#FF9933", color: "#0A0A0A", borderColor: "#FF9933" }} data-testid="dashboard-edit-question">
                Manage →
              </Link>
            </div>
          </Card>

          {data.trending_message && (
            <Card title="Trending right now" testId="card-trending">
              <div className="flex items-start gap-3">
                <TrendingUp size={20} strokeWidth={3} style={{ color: "#E3000F" }} className="shrink-0 mt-1" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium leading-relaxed" data-testid="trending-message-text">
                    "{data.trending_message.message}"
                  </p>
                  <p className="text-[10px] uppercase font-bold tracking-[0.3em] mt-2 text-neutral-400">
                    {data.top_category || data.trending_message.category} · {data.trending_message.upvotes} upvotes
                  </p>
                </div>
              </div>
            </Card>
          )}

          <Card title="Recent activity" testId="card-recent">
            <ul className="divide-y divide-[#1f1f1f]">
              {(data.recent_activity || []).map((m) => (
                <li key={m.id} className="py-3" data-testid={`recent-${m.id}`}>
                  <p className="text-sm font-medium leading-snug line-clamp-2">{m.message}</p>
                  <p className="text-[10px] uppercase font-bold tracking-[0.3em] mt-1 text-neutral-400">
                    {m.category} · {m.upvotes} upvotes · {m.created_at?.slice(0, 16).replace("T", " ")}
                  </p>
                </li>
              ))}
              {(!data.recent_activity || data.recent_activity.length === 0) && (
                <li className="py-6 text-center text-xs text-neutral-500 uppercase tracking-[0.3em]">No recent activity</li>
              )}
            </ul>
          </Card>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <QuickLink to="/admin/messages" Icon={MessageSquare} label="Messages" />
            <QuickLink to="/admin/moderation" Icon={ShieldAlert} label="Moderation" />
            <QuickLink to="/admin/spam" Icon={Ban} label="Spam" />
            <QuickLink to="/admin/content-studio" Icon={Sparkles} label="Content" />
          </div>
        </>
      )}
    </div>
  );
};

const CampaignPicker = ({ polls, value, onChange }) => (
  <label className="flex flex-col items-end gap-1" data-testid="campaign-picker">
    <span className="text-[10px] uppercase font-extrabold tracking-[0.35em] text-neutral-400">
      Choose Campaign
    </span>
    <div className="relative">
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="border-2 border-[#1f1f1f] bg-[#0F0F0F] text-[#FDFBF7] pl-3 pr-10 py-2 text-sm font-bold uppercase tracking-[0.15em] appearance-none cursor-pointer hover:border-[#FF9933] focus:border-[#FF9933] outline-none min-w-[260px]"
        data-testid="campaign-picker-select"
      >
        <option value={ALL_POLLS}>All polls</option>
        {polls.map((p) => (
          <option key={p.slug} value={p.slug}>
            {p.title || p.headline}
          </option>
        ))}
      </select>
      <ChevronDown size={14} strokeWidth={3} className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" style={{ color: "#FF9933" }} />
    </div>
  </label>
);

const QuickLink = ({ to, Icon, label }) => (
  <Link
    to={to}
    className="border-2 border-[#1f1f1f] hover:border-[#FF9933] p-4 flex items-center gap-3 transition-colors"
    style={{ background: "#141414" }}
    data-testid={`quick-link-${label.toLowerCase().replace(/\s+/g, '-')}`}
  >
    <Icon size={18} strokeWidth={3} style={{ color: "#FF9933" }} />
    <span className="text-xs uppercase font-extrabold tracking-[0.25em]">{label}</span>
  </Link>
);

export default Dashboard;
